import { config } from '@terminal/config';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { useUser } from '@terminal/stores/use-user/useUser';
import { usePostHog as usePostHogBase } from 'posthog-js/react';

import { PosthogTrackingEventName, PosthogTrackingSource } from './constants';

export type CaptureUserProperties = {
  properties: {
    user_role?: string;
    user_firm_id?: string;
    user_created_at?: string;
    user_on_trial?: boolean;
    user_subscription?: string;
    user_subscription_next_payment?: string;
  };
  setOnce?: boolean;
};

const SYNTHETIC_USER_EMAIL_PREFIX = 'k6user+';

export const usePostHog = () => {
  const posthog = usePostHogBase();
  const shouldEnableTracking = (email: string) =>
    ['staging', 'prod'].includes(config.app.environment) &&
    !email.startsWith(SYNTHETIC_USER_EMAIL_PREFIX);

  const locale = useLanguageStore(state => state.languageSetting);

  const identifyUser = (userId: string, email: string) => {
    if (shouldEnableTracking(email)) {
      posthog.identify(userId, { locale, reflexivity_user_id: userId });
    }
  };

  const captureTrackingEvent = ({
    eventName,
    properties,
  }: {
    eventName: PosthogTrackingEventName;
    properties: Record<string, unknown> & {
      source: PosthogTrackingSource | null;
    };
  }) => {
    if (shouldEnableTracking(useUser.getState().user?.email ?? '')) {
      posthog.capture(eventName, properties);
    }
  };

  const captureUserProperties = ({
    properties,
    setOnce = false,
  }: CaptureUserProperties) => {
    if (shouldEnableTracking(useUser.getState().user?.email ?? '')) {
      const payload = setOnce
        ? [undefined, properties]
        : [properties, undefined];
      posthog.setPersonProperties(...payload);
    }
  };

  return {
    identifyUser,
    captureTrackingEvent,
    captureUserProperties,
  };
};
